import React, { useState, useEffect, useCallback } from 'react';
import { API_ORIGIN } from '../config/env';
import { authFetch } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './PasswordVault.css';

const API_BASE = API_ORIGIN; // config/env.js (P1-B)

const CATEGORIES = ['All', 'Transportation', 'DOE', 'Insurance', 'Payroll', 'Fleet', 'IT', 'Other'];

const PasswordVault = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // PIN Gate state
  const [pinVerified, setPinVerified] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [pinCheckDone, setPinCheckDone] = useState(false);
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  // Data state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('entries');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    site_name: '', site_url: '', username: '', password: '', category: 'Other', notes: ''
  });
  const [showPassword, setShowPassword] = useState({});
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Audit state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // P1-B: the bearer token is attached by authFetch (session module); no storage reads here.
  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json'
  }), []);

  // ===== AUTO-DETECT PIN STATUS ON MOUNT =====
  useEffect(() => {
    if (sessionStorage.getItem('vault_verified') === 'true') {
      setPinVerified(true);
      setPinCheckDone(true);
      return;
    }
    // Probe the verify endpoint with a dummy to check if PIN is set
    const checkPinStatus = async () => {
      try {
        const res = await authFetch(`${API_BASE}/api/vault/verify-pin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin: '____probe____' })
        });
        const data = await res.json();
        if (data.needsSetup) {
          setNeedsSetup(true);
        }
      } catch (err) {
        // ignore
      }
      setPinCheckDone(true);
    };
    checkPinStatus();
  }, []);

  // ===== PIN VERIFICATION =====
  const verifyPin = async (e) => {
    e.preventDefault();
    setPinError('');
    setPinLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/vault/verify-pin`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (res.ok) {
        setPinVerified(true);
        sessionStorage.setItem('vault_verified', 'true');
      } else if (data.needsSetup) {
        setNeedsSetup(true);
      } else {
        setPinError(data.message || 'Invalid PIN');
      }
    } catch (err) {
      setPinError('Connection error');
    }
    setPinLoading(false);
  };

  const setupPin = async (e) => {
    e.preventDefault();
    if (newPin !== confirmPin) {
      setPinError('PINs do not match');
      return;
    }
    if (newPin.length < 4) {
      setPinError('PIN must be at least 4 characters');
      return;
    }
    setPinError('');
    setPinLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/vault/set-pin`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ pin: newPin })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Vault PIN set successfully');
        setNeedsSetup(false);
        setPin('');
        setNewPin('');
        setConfirmPin('');
      } else {
        setPinError(data.message || 'Failed to set PIN');
      }
    } catch (err) {
      setPinError('Connection error');
    }
    setPinLoading(false);
  };

  // ===== FETCH ENTRIES =====
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'All') params.append('category', categoryFilter);
      if (search) params.append('search', search);

      const res = await authFetch(`${API_BASE}/api/vault/entries?${params}`, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setEntries(data.entries || []);
      }
    } catch (err) {
      console.error('Fetch entries error:', err);
    }
    setLoading(false);
  }, [categoryFilter, search, getHeaders]);

  useEffect(() => {
    if (pinVerified) fetchEntries();
  }, [pinVerified, fetchEntries]);

  // ===== SHOW/HIDE PASSWORD =====
  const togglePassword = async (entryId) => {
    if (visiblePasswords[entryId]) {
      setVisiblePasswords(prev => ({ ...prev, [entryId]: null }));
      return;
    }
    try {
      const res = await authFetch(`${API_BASE}/api/vault/entries/${entryId}`, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setVisiblePasswords(prev => ({ ...prev, [entryId]: data.password }));
      } else {
        toast.error(data.message || 'Failed to load password');
      }
    } catch (err) {
      toast.error('Failed to load password');
    }
  };

  // ===== COPY PASSWORD =====
  const copyPassword = async (entryId) => {
    try {
      let password = visiblePasswords[entryId];
      if (!password) {
        const res = await authFetch(`${API_BASE}/api/vault/entries/${entryId}`, {
          headers: getHeaders()
        });
        const data = await res.json();
        if (!res.ok) { toast.error('Failed to copy'); return; }
        password = data.password;
      }
      await navigator.clipboard.writeText(password);
      setCopiedId(entryId);
      setTimeout(() => setCopiedId(null), 2000);

      authFetch(`${API_BASE}/api/vault/entries/${entryId}/copy`, {
        method: 'POST',
        headers: getHeaders()
      }).catch(() => {});

      toast.success('Password copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // ===== CREATE / UPDATE =====
  const openAddModal = () => {
    setEditingEntry(null);
    setFormData({ site_name: '', site_url: '', username: '', password: '', category: 'Other', notes: '' });
    setShowPassword({});
    setShowModal(true);
  };

  const openEditModal = async (entry) => {
    try {
      const res = await authFetch(`${API_BASE}/api/vault/entries/${entry.id}`, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setEditingEntry(entry);
        setFormData({
          site_name: data.site_name || '',
          site_url: data.site_url || '',
          username: data.username || '',
          password: data.password || '',
          category: data.category || 'Other',
          notes: data.notes || ''
        });
        setShowPassword({ modal: false });
        setShowModal(true);
      } else {
        toast.error(data.message || 'Failed to load entry');
      }
    } catch (err) {
      toast.error('Failed to load entry');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.site_name || !formData.password) {
      toast.error('Site name and password are required');
      return;
    }
    setSaving(true);
    try {
      const url = editingEntry
        ? `${API_BASE}/api/vault/entries/${editingEntry.id}`
        : `${API_BASE}/api/vault/entries`;
      const method = editingEntry ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(editingEntry ? 'Entry updated' : 'Entry created');
        setShowModal(false);
        fetchEntries();
      } else {
        toast.error(data.message || 'Save failed');
      }
    } catch (err) {
      toast.error('Save failed');
    }
    setSaving(false);
  };

  // ===== DELETE =====
  const handleDelete = async (entryId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/vault/entries/${entryId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Entry deleted');
        setDeleteConfirm(null);
        fetchEntries();
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // ===== AUDIT LOG =====
  const fetchAuditLog = async () => {
    setAuditLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/vault/audit?limit=100`, {
        headers: getHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setAuditLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Fetch audit error:', err);
    }
    setAuditLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'audit' && pinVerified && isAdmin) fetchAuditLog();
  }, [activeTab, pinVerified, isAdmin]);

  const getAuditActionClass = (action) => {
    if (action.includes('VIEW')) return 'view';
    if (action.includes('COPY')) return 'copy';
    if (action.includes('CREATE')) return 'create';
    if (action.includes('UPDATE')) return 'update';
    if (action.includes('DELETE')) return 'delete';
    if (action.includes('PIN')) return 'pin';
    return '';
  };

  const formatAction = (action) => {
    return action.replace('VAULT_', '').replace(/_/g, ' ');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // ===== PIN GATE RENDER =====
  if (!pinVerified) {
    return (
      <div className="vault-container">
        <div className="vault-pin-gate">
          <div className="pin-icon">{'\uD83D\uDD10'}</div>
          <h2>Password Vault</h2>

          {!pinCheckDone ? (
            <p>Checking vault status...</p>
          ) : needsSetup && isAdmin ? (
            <>
              <p>No master PIN has been set yet. Create one to get started.</p>
              <form onSubmit={setupPin} className="pin-setup-form">
                <label>Set up a master PIN for the vault</label>
                <input
                  type="password"
                  className="pin-input"
                  placeholder="New PIN"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  maxLength={8}
                  autoFocus
                />
                <input
                  type="password"
                  className="pin-input"
                  placeholder="Confirm PIN"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  maxLength={8}
                />
                {pinError && <div className="pin-error">{pinError}</div>}
                <button type="submit" className="vault-btn-primary" disabled={pinLoading}>
                  {pinLoading ? 'Setting...' : 'Set Vault PIN'}
                </button>
              </form>
            </>
          ) : needsSetup ? (
            <p style={{ color: '#ff6b6b' }}>No vault PIN configured. Ask an admin to set one up.</p>
          ) : (
            <>
              <p>Enter the master PIN to access the vault</p>
              <form onSubmit={verifyPin}>
                <div className="pin-input-group">
                  <input
                    type="password"
                    className="pin-input"
                    placeholder={'\u2022\u2022\u2022\u2022'}
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    maxLength={8}
                    autoFocus
                  />
                  <button type="submit" className="vault-btn-primary" disabled={pinLoading}>
                    {pinLoading ? '...' : 'Unlock'}
                  </button>
                </div>
                {pinError && <div className="pin-error">{pinError}</div>}
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  // ===== MAIN VAULT RENDER =====
  return (
    <div className="vault-container">
      <h1>{'\uD83D\uDD10'} Password Vault</h1>
      <p className="vault-subtitle">Securely stored company credentials {'\u2014'} all access is logged</p>

      {/* Tabs */}
      <div className="vault-tabs">
        <button
          className={`vault-tab ${activeTab === 'entries' ? 'active' : ''}`}
          onClick={() => setActiveTab('entries')}
        >
          Credentials ({entries.length})
        </button>
        {isAdmin && (
          <button
            className={`vault-tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Audit Log
          </button>
        )}
      </div>

      {activeTab === 'entries' ? (
        <>
          {/* Toolbar */}
          <div className="vault-toolbar">
            <input
              type="text"
              className="vault-search"
              placeholder="Search credentials..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="vault-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button className="vault-btn-primary" onClick={openAddModal}>
              + Add Credential
            </button>
          </div>

          {/* Entries Grid */}
          {loading ? (
            <div className="vault-empty">Loading...</div>
          ) : entries.length === 0 ? (
            <div className="vault-empty">
              <div className="empty-icon">{'\uD83D\uDD12'}</div>
              <p>No credentials stored yet</p>
              <button className="vault-btn-primary" onClick={openAddModal}>
                Add First Credential
              </button>
            </div>
          ) : (
            <div className="vault-entries">
              {entries.map(entry => (
                <div key={entry.id} className="vault-card">
                  <div className="vault-card-header">
                    <h3 className="vault-card-title">{entry.site_name}</h3>
                    <span className="vault-card-category">{entry.category}</span>
                  </div>

                  {entry.site_url && (
                    <a
                      href={entry.site_url.startsWith('http') ? entry.site_url : `https://${entry.site_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="vault-card-url"
                    >
                      {entry.site_url}
                    </a>
                  )}

                  <div className="vault-card-field">
                    <span className="field-label">User:</span>
                    <span className="field-value">{entry.username || '\u2014'}</span>
                  </div>

                  <div className="vault-card-field">
                    <span className="field-label">Password:</span>
                    <span className="field-value field-password">
                      {visiblePasswords[entry.id] || '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                    </span>
                  </div>

                  {entry.notes && (
                    <div className="vault-card-notes">{entry.notes}</div>
                  )}

                  <div className="vault-card-meta">
                    Added by {entry.added_by_name || 'Unknown'} {'\u00b7'} {formatDate(entry.updated_at)}
                  </div>

                  <div className="vault-card-actions">
                    <button
                      className="vault-btn-sm"
                      onClick={() => togglePassword(entry.id)}
                    >
                      {visiblePasswords[entry.id] ? 'Hide' : 'Show'}
                    </button>
                    <button
                      className={`vault-btn-sm ${copiedId === entry.id ? 'copied' : ''}`}
                      onClick={() => copyPassword(entry.id)}
                    >
                      {copiedId === entry.id ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      className="vault-btn-sm"
                      onClick={() => openEditModal(entry)}
                    >
                      Edit
                    </button>
                    {isAdmin && (
                      deleteConfirm === entry.id ? (
                        <>
                          <button
                            className="vault-btn-sm danger"
                            onClick={() => handleDelete(entry.id)}
                          >
                            Confirm
                          </button>
                          <button
                            className="vault-btn-sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          className="vault-btn-sm danger"
                          onClick={() => setDeleteConfirm(entry.id)}
                        >
                          Delete
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Audit Log Tab */
        <div>
          {auditLoading ? (
            <div className="vault-empty">Loading audit log...</div>
          ) : auditLogs.length === 0 ? (
            <div className="vault-empty">No audit events yet</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="vault-audit-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => {
                    const details = typeof log.details === 'string' ? JSON.parse(log.details) : (log.details || {});
                    return (
                      <tr key={log.id}>
                        <td>{formatDate(log.created_at)}</td>
                        <td>{log.user_name || 'System'}</td>
                        <td>
                          <span className={`vault-audit-action ${getAuditActionClass(log.action)}`}>
                            {formatAction(log.action)}
                          </span>
                        </td>
                        <td style={{ color: '#888', fontSize: '0.8rem' }}>
                          {details.site_name || ''}
                          {details.password_changed ? ' (password changed)' : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="vault-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="vault-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingEntry ? 'Edit Credential' : 'Add Credential'}</h2>
            <form onSubmit={handleSave}>
              <div className="vault-form-group">
                <label>Site Name *</label>
                <input
                  type="text"
                  value={formData.site_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, site_name: e.target.value }))}
                  placeholder="e.g. DOE InfoHub, BusHive, Geotab"
                  autoFocus
                />
              </div>

              <div className="vault-form-group">
                <label>URL</label>
                <input
                  type="text"
                  value={formData.site_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, site_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>

              <div className="vault-form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Username or email"
                />
              </div>

              <div className="vault-form-group">
                <label>Password *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword.modal ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={editingEntry ? 'Leave unchanged or enter new' : 'Enter password'}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(prev => ({ ...prev, modal: !prev.modal }))}
                  >
                    {showPassword.modal ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="vault-form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  {CATEGORIES.filter(c => c !== 'All').map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="vault-form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="vault-modal-actions">
                <button type="button" className="vault-btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="vault-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingEntry ? 'Update' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordVault;
